import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Row, Col } from 'react-bootstrap';
import adminSchemaApi from '../api/adminSchemaApi';
import { formatCurrencyLabel } from '../../utils/formatUtils';
import '../styles/admin-theme.css';

/** Parse YYYY-MM-DD as local date (no UTC shift). */
function parseLocalDate(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const parts = dateStr.trim().slice(0, 10).split('-').map(Number);
  if (parts.length !== 3) return null;
  const [y, m, d] = parts;
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

/** Format date as YYYY-MM-DD in local time (no UTC shift). */
function toLocalDateString(date) {
  if (!date || !date.getTime) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Billing cycle period in days: monthly = 30, quarterly = 90, half_yearly = 180, yearly = 365. */
const BILLING_CYCLE_DAYS = {
  monthly: 30,
  quarterly: 90,
  half_yearly: 180,
  yearly: 365,
};

/** Compute end date from start date and billing cycle. End = last day of period (start + period days - 1). */
function getEndDateFromBillingCycle(startDateStr, billingCycle) {
  if (!startDateStr || !billingCycle) return '';
  const start = parseLocalDate(startDateStr);
  if (!start) return '';
  const days = BILLING_CYCLE_DAYS[billingCycle];
  if (days == null) return '';
  const end = new Date(start);
  end.setDate(end.getDate() + days - 1);
  return toLocalDateString(end);
}

const LICENSE_STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'EXPIRED', label: 'Expired' },
];

const emptyLicenseRow = () => ({
  license_type_id: '',
  quantity: 1,
  start_date: '',
  status: 'DRAFT',
});

const NewSubscriptionModal = ({ show, onHide, companyId, onSuccess, initialSubscription }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [plans, setPlans] = useState([]);
  const [pricebooks, setPricebooks] = useState([]);
  const [planLicenses, setPlanLicenses] = useState([]);
  const [pricebookEntries, setPricebookEntries] = useState([]);
  const [form, setForm] = useState({
    billing_cycle: 'monthly',
    start_date: '',
    end_date: '',
    status: 'DRAFT',
    plan_id: '',
    pricebook_id: '',
  });
  const [licenses, setLicenses] = useState([emptyLicenseRow()]);
  const isEdit = !!initialSubscription;
  const lastSyncedAllowedIdsRef = useRef(null);

  useEffect(() => {
    if (show) {
      Promise.all([
        adminSchemaApi.fetchLicenseTypes().then((list) => Array.isArray(list) ? list : []).catch(() => []),
        adminSchemaApi.fetchPlans().then((list) => Array.isArray(list) ? list : []).catch(() => []),
      ]).then(([lt, p]) => {
        setLicenseTypes(lt);
        setPlans(p);
      });
      setPlanLicenses([]);
      setPricebookEntries([]);
      setPricebooks([]);
      lastSyncedAllowedIdsRef.current = null;
    }
  }, [show]);

  useEffect(() => {
    if (show && form.plan_id) {
      adminSchemaApi
        .fetchPricebooksByPlanId(form.plan_id)
        .then((list) => setPricebooks(Array.isArray(list) ? list : []))
        .catch(() => setPricebooks([]));
    } else {
      setPricebooks([]);
    }
  }, [show, form.plan_id]);

  useEffect(() => {
    if (show && form.plan_id) {
      adminSchemaApi
        .fetchPlanLicenses(form.plan_id)
        .then((list) => setPlanLicenses(Array.isArray(list) ? list : []))
        .catch(() => setPlanLicenses([]));
    } else {
      setPlanLicenses([]);
    }
  }, [show, form.plan_id]);

  useEffect(() => {
    if (show && form.plan_id && form.pricebook_id) {
      adminSchemaApi
        .fetchPricebookEntries(form.pricebook_id)
        .then((list) => setPricebookEntries(Array.isArray(list) ? list : []))
        .catch(() => setPricebookEntries([]));
    } else {
      setPricebookEntries([]);
    }
  }, [show, form.plan_id, form.pricebook_id]);

  const allowedLicenseTypeIds = useMemo(() => {
    const planIds = new Set((planLicenses || []).map((pl) => pl.license_type_id));
    const withPrice = new Set(
      (pricebookEntries || [])
        .filter((e) => e.plan_id === form.plan_id)
        .map((e) => e.productid)
    );
    return new Set([...planIds].filter((id) => withPrice.has(id)));
  }, [planLicenses, pricebookEntries, form.plan_id]);
  const allowedLicenseTypes = useMemo(
    () => licenseTypes.filter((lt) => allowedLicenseTypeIds.has(lt.id)),
    [licenseTypes, allowedLicenseTypeIds]
  );

  useEffect(() => {
    if (!show || isEdit || !form.plan_id || !form.pricebook_id) return;
    const currentKey = [...allowedLicenseTypeIds].sort().join(',');
    const lastKey = lastSyncedAllowedIdsRef.current;
    if (lastKey === currentKey) return;
    lastSyncedAllowedIdsRef.current = currentKey;
    const rows =
      allowedLicenseTypeIds.size > 0
        ? [...allowedLicenseTypeIds].map((license_type_id) => ({
            license_type_id,
            quantity: 1,
            start_date: form.start_date || '',
            status: 'DRAFT',
          }))
        : [{ ...emptyLicenseRow(), start_date: form.start_date || '' }];
    setLicenses(rows);
  }, [show, isEdit, form.plan_id, form.pricebook_id, form.start_date, allowedLicenseTypeIds]);

  useEffect(() => {
    if (show && initialSubscription) {
      const statusVal = (initialSubscription.status || 'ACTIVE').toUpperCase();
      const status = ['DRAFT', 'ACTIVE', 'INACTIVE', 'CANCELLED', 'EXPIRED'].includes(statusVal) ? statusVal : 'ACTIVE';
      setForm({
        billing_cycle: initialSubscription.billing_cycle || 'monthly',
        start_date: (initialSubscription.start_date || '').slice(0, 10),
        end_date: (initialSubscription.end_date || '').slice(0, 10),
        status,
        plan_id: initialSubscription.plan_id || '',
        pricebook_id: initialSubscription.pricebook_id || '',
      });
      const existingLicenses = Array.isArray(initialSubscription.licenses)
        ? initialSubscription.licenses.map((row) => {
            const s = (row.status || 'DRAFT').toUpperCase();
            const status = ['DRAFT', 'ACTIVE', 'INACTIVE', 'CANCELLED', 'EXPIRED'].includes(s) ? s : 'DRAFT';
            return {
              id: row.id,
              license_type_id: row.license_type_id || '',
              quantity: row.quantity ?? 1,
              start_date: (row.start_date || '').slice(0, 10),
              status,
            };
          })
        : [];
      setLicenses(existingLicenses.length > 0 ? existingLicenses : [emptyLicenseRow()]);
      setError('');
    }
  }, [show, initialSubscription]);

  const resetForm = () => {
    setForm({
      billing_cycle: 'monthly',
      start_date: '',
      end_date: '',
      status: 'DRAFT',
      plan_id: '',
      pricebook_id: '',
    });
    setLicenses([emptyLicenseRow()]);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'plan_id') {
        next.pricebook_id = '';
      }
      if (name === 'billing_cycle' && prev.start_date) {
        next.end_date = getEndDateFromBillingCycle(prev.start_date, value);
      } else if (name === 'start_date') {
        next.end_date = getEndDateFromBillingCycle(value, next.billing_cycle);
      }
      return next;
    });
    if (name === 'plan_id' || name === 'pricebook_id') {
      lastSyncedAllowedIdsRef.current = null;
      setLicenses([{ ...emptyLicenseRow(), start_date: form.start_date || '' }]);
    }
    if (name === 'start_date') {
      setLicenses((prev) =>
        prev.map((row) => ({
          ...row,
          start_date: row.start_date || value,
        }))
      );
    }
  };

  const addLicenseRow = () => {
    setLicenses((prev) => [...prev, { ...emptyLicenseRow(), start_date: form.start_date || '' }]);
  };

  const removeLicenseRow = (index) => {
    setLicenses((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const updateLicenseRow = (index, field, value) => {
    setLicenses((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!companyId) {
      setError('Company is missing.');
      return;
    }
    if (!form.start_date || !form.end_date) {
      setError('Start date and end date are required.');
      return;
    }
    if (!form.plan_id) {
      setError('Plan is required.');
      return;
    }
    if (!form.pricebook_id) {
      setError('Pricebook is required.');
      return;
    }
    if (new Date(form.end_date) < new Date(form.start_date)) {
      setError('End date must be on or after start date.');
      return;
    }
    const validLicenses = licenses.filter(
      (row) => row.license_type_id && Number(row.quantity) >= 0
    );
    if (validLicenses.length > 0) {
      const subActive = (form.status || '').toUpperCase() === 'ACTIVE';
      for (const row of validLicenses) {
        const act = row.start_date || form.start_date;
        if (new Date(act) < new Date(form.start_date) || new Date(act) > new Date(form.end_date)) {
          setError('Each license activation date must be within the subscription period.');
          return;
        }
        if (!subActive && (row.status || 'DRAFT').toUpperCase() === 'ACTIVE') {
          setError('License cannot be ACTIVE until subscription is ACTIVE.');
          return;
        }
      }
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        billing_cycle: form.billing_cycle,
        start_date: form.start_date,
        end_date: form.end_date,
        status: form.status,
        plan_id: form.plan_id,
        pricebook_id: form.pricebook_id,
      };
      if (!isEdit && validLicenses.length > 0) {
        payload.licenses = validLicenses.map((row) => ({
          license_type_id: row.license_type_id,
          quantity: Math.max(0, Number(row.quantity)),
          start_date: row.start_date || form.start_date,
          status: (row.status || 'DRAFT').toUpperCase(),
        }));
      }
      if (isEdit) {
        await adminSchemaApi.updateSubscription(companyId, initialSubscription.id, payload);
        if (validLicenses.length > 0) {
          const tasks = validLicenses.map((row) => {
            const rowPayload = {
              license_type_id: row.license_type_id,
              quantity: Math.max(0, Number(row.quantity)),
              start_date: row.start_date || form.start_date,
              status: (row.status || 'DRAFT').toUpperCase(),
            };
            if (row.id) {
              return adminSchemaApi.updateLicense(
                companyId,
                initialSubscription.id,
                row.id,
                rowPayload
              );
            }
            return adminSchemaApi.addLicenseToSubscription(
              companyId,
              initialSubscription.id,
              rowPayload
            );
          });
          await Promise.all(tasks);
        }
      } else {
        await adminSchemaApi.createSubscription(companyId, payload);
      }
      resetForm();
      onHide();
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
    } catch (err) {
      setError(err?.message || (isEdit ? 'Failed to update subscription.' : 'Failed to create subscription.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="admin-title">
          {isEdit ? 'Edit Subscription' : 'New Subscription'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        <Form onSubmit={handleSubmit}>
          <Card className="mb-3 border-0 shadow-sm">
            <Card.Body>
              <Card.Title className="admin-detail-widget-title mb-3">Subscription period</Card.Title>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Billing cycle</Form.Label>
                    <Form.Select
                      name="billing_cycle"
                      value={form.billing_cycle}
                      onChange={handleChange}
                      required
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="half_yearly">Half Yearly</option>
                      <option value="yearly">Yearly</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={form.status}
                      onChange={handleChange}
                    >
                      <option value="DRAFT">Draft</option>
                      <option value="ACTIVE" disabled={isEdit ? false : true}>
                        Active (requires paid invoice)
                      </option>
                      <option value="INACTIVE">Inactive</option>
                      <option value="CANCELLED">Cancelled</option>
                      <option value="EXPIRED">Expired</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start date</Form.Label>
                    <Form.Control
                      type="date"
                      name="start_date"
                      value={form.start_date}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>End date</Form.Label>
                    <Form.Control
                      type="date"
                      name="end_date"
                      value={form.end_date}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Plan</Form.Label>
                    <Form.Select
                      name="plan_id"
                      value={form.plan_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select plan</option>
                      {plans.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name || p.code} ({p.code})
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted small">Plan is required.</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Pricebook</Form.Label>
                    <Form.Select
                      name="pricebook_id"
                      value={form.pricebook_id}
                      onChange={handleChange}
                      required
                      disabled={!form.plan_id}
                    >
                      <option value="">
                        {!form.plan_id ? 'Select plan first' : pricebooks.length === 0 ? 'No pricebooks for this plan' : 'Select pricebook'}
                      </option>
                      {pricebooks.filter((pb) => pb.is_active !== false).map((pb) => (
                        <option key={pb.id} value={pb.id}>
                          {pb.name || pb.id} ({pb.currency ? formatCurrencyLabel(pb.currency) : '-'})
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted small">
                      {form.plan_id
                        ? 'Pricebooks with entries for this plan. Add entries in Plan Detail or Pricebook first.'
                        : 'Select a plan to see available pricebooks.'}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-3 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Card.Title className="admin-detail-widget-title mb-0">Licenses</Card.Title>
                  <Button type="button" variant="outline-primary" size="sm" onClick={addLicenseRow}>
                    + Add license
                  </Button>
                </div>
                <p className="admin-subtitle small mb-3">
                  Add license types with quantity and activation date. Licenses are optional.
                </p>
                {licenses.map((row, index) => (
                  <Row key={index} className="align-items-end mb-2 pb-2 border-bottom border-light">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="small">License type</Form.Label>
                        <Form.Select
                          value={row.license_type_id}
                          onChange={(e) =>
                            updateLicenseRow(index, 'license_type_id', e.target.value)
                          }
                          disabled={!form.plan_id || !form.pricebook_id}
                        >
                          <option value="">
                            {form.plan_id && form.pricebook_id ? 'Select type' : 'Select plan and pricebook first'}
                          </option>
                          {allowedLicenseTypes.map((lt) => (
                            <option key={lt.id} value={lt.id}>
                              {lt.name || lt.code}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label className="small">Quantity</Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          value={row.quantity}
                          onChange={(e) =>
                            updateLicenseRow(index, 'quantity', e.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label className="small">Status</Form.Label>
                        <Form.Select
                          value={row.status || 'DRAFT'}
                          onChange={(e) =>
                            updateLicenseRow(index, 'status', e.target.value)
                          }
                        >
                          {LICENSE_STATUS_OPTIONS.map((opt) => (
                            <option
                              key={opt.value}
                              value={opt.value}
                              disabled={opt.value === 'ACTIVE' && (form.status || '').toUpperCase() !== 'ACTIVE'}
                            >
                              {opt.value === 'ACTIVE' && (form.status || '').toUpperCase() !== 'ACTIVE' ? `${opt.label} (subscription must be active)` : opt.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small">Activation date</Form.Label>
                        <Form.Control
                          type="date"
                          value={row.start_date || form.start_date}
                          onChange={(e) =>
                            updateLicenseRow(index, 'start_date', e.target.value)
                          }
                          min={form.start_date}
                          max={form.end_date}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={1} className="text-end">
                      <Button
                        type="button"
                        variant="outline-danger"
                        size="sm"
                        className="px-2"
                        onClick={() => removeLicenseRow(index)}
                      title={row.id ? 'Remove not available for existing licenses' : 'Remove'}
                      disabled={licenses.length === 1 || !!row.id}
                      >
                        ×
                      </Button>
                    </Col>
                  </Row>
                ))}
              </Card.Body>
            </Card>

          <div className="d-flex justify-content-end gap-2 pt-2">
            <Button variant="outline-secondary" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" className="admin-primary-btn" disabled={saving}>
              {saving ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save' : 'Create subscription')}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default NewSubscriptionModal;
